// hooks.ts - React Query hooks for Critical Parameters

import { useQueryClient } from "@tanstack/react-query";
import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/hooks/use-toast";
import { criticalParametersApi, transformCriticalParameter } from "./api";

// Get all critical parameters
export const useCriticalParameters = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [QueryKeyFactory.module.ax.criticalParameters.list(), searchQuery],
    async () => {
      const response = await criticalParametersApi.getCriticalParameters(
        utilityId,
        searchQuery
      );

      return {
        parameters: response.result.map(transformCriticalParameter),
        totalWeightage: response.totalWeightage,
        isValidModel: response.isValidModel,
      };
    },
    {
      enabled: !!utilityId,
    }
  );
};

// Create critical parameter
export const useCreateCriticalParameter = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      name: string;
      weightScore: number;
      description: string;
    }) => {
      const payload = {
        name: data.name.trim(),
        score: data.weightScore,
        code: data.name.trim(),
        remoteUtilityId: utilityId,
        description: data.description.trim(),
      };

      return criticalParametersApi.createCriticalParameter(payload);
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.ax.criticalParameters.list()],
        });
        toast({
          title: "Success",
          description: "Critical parameter created successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create critical parameter",
          variant: "destructive",
        });
      },
    }
  );
};

// Update critical parameter
export const useUpdateCriticalParameter = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      code: string;
      name: string;
      weightScore: number;
      description: string;
    }) => {
      const payload = {
        name: data.name.trim(),
        score: data.weightScore,
        code: data.code,
        remoteUtilityId: utilityId,
        description: data.description.trim(),
      };

      return criticalParametersApi.updateCriticalParameter(payload);
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.ax.criticalParameters.list()],
        });
        toast({
          title: "Success",
          description: "Critical parameter updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update critical parameter",
          variant: "destructive",
        });
      },
    }
  );
};

// Delete critical parameter
export const useDeleteCriticalParameter = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { code: string; name: string }) => {
      const payload = {
        code: data.code,
        remoteUtilityId: utilityId,
      };

      return criticalParametersApi.deleteCriticalParameter(payload);
    },
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.ax.criticalParameters.list()],
        });
        toast({
          title: "Success",
          description: `${variables.name} has been deleted successfully`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to delete critical parameter",
          variant: "destructive",
        });
      },
    }
  );
};
